<template>
  <div id="responsive-overlay" @click="mainContentFn"></div>
  <aside
      class="app-sidebar sticky"
      :class="{ 'sticky-pin': menuOverflowed }"
      id="sidebar"
  >
    <div class="main-sidebar-header">
      <NuxtLink to="/" class="header-logo">
        <img src="/images/logo.png" alt="logo" class="desktop-logo" />
        <img
            src="/images/brand-logos/toggle-logo.png"
            alt="logo"
            class="toggle-logo"
        />
        <img src="/images/logo.png" alt="logo" class="desktop-dark" />
        <img
            src="/images/brand-logos/toggle-dark.png"
            alt="logo"
            class="toggle-dark"
        />
        <img src="/images/logo.png" alt="logo" class="desktop-white" />
        <img
            src="/images/brand-logos/toggle-white.png"
            alt="logo"
            class="toggle-white"
        />
      </NuxtLink>
    </div>

    <PerfectScrollbar class="main-sidebar" id="sidebar-scroll">
      <div class="hor-scroll">
        <nav class="main-menu-container nav nav-pills flex-column sub-open">
          <div class="slide-left" id="slide-left" @click="leftArrowFn">
            <svg
                xmlns="http://www.w3.org/2000/svg"
                fill="#7b8191"
                width="24"
                height="24"
                viewBox="0 0 24 24"
            >
              <path
                  d="M13.293 6.293 7.586 12l5.707 5.707 1.414-1.414L10.414 12l4.293-4.293z"
              />
            </svg>
          </div>

          <ul class="main-menu">
            <!-- Lien Notifications mobile — même kill-switch que la cloche header (store.studentPushNotificationsEnabled) -->
            <li v-if="isMobile && profileStore.isStudent && notificationsStore.studentPushNotificationsEnabled" class="slide d-lg-none">
              <NuxtLink
                to="/admin/notifications"
                class="side-menu__item"
              >
                <i class="ti ti-bell side-menu__icon"></i>
                <span class="side-menu__label d-flex align-items-center gap-1">
                  <span>Notifications</span>
                  <span
                    v-if="notificationsStore.unreadCount > 0"
                    class="badge bg-primary text-white rounded-pill ms-1 px-2 py-0"
                    style="font-size: 0.7rem;"
                  >
                    {{ notificationsStore.unreadCount > 99 ? '99+' : notificationsStore.unreadCount }}
                  </span>
                </span>
              </NuxtLink>
            </li>
            <li
                v-for="(mainmenuItem, index) in visibleMenu"
                :key="mainmenuItem.key || index"
                :class="[
                mainmenuItem.menuTitle ? 'slide__category' : '',
                mainmenuItem?.type === 'link' ? 'slide' : '',
                mainmenuItem?.type === 'empty' ? 'slide' : '',
                mainmenuItem?.type === 'sub' ? 'slide has-sub' : '',
                mainmenuItem?.active ? 'open' : '',
                mainmenuItem?.selected ? 'active' : '',
              ]"
            >
              <!-- Catégorie (section) -->
              <template v-if="mainmenuItem.menuTitle">
                <span class="category-name">{{ mainmenuItem.menuTitle }}</span>
              </template>

              <!-- Lien simple -->
              <template v-else-if="mainmenuItem?.type === 'link'">
                <NuxtLink
                    :to="mainmenuItem.path"
                    class="side-menu__item"
                    :class="mainmenuItem.selected ? 'active' : ''"
                    @click="setSubmenu($event, mainmenuItem)"
                >
                  <i :class="`${mainmenuItem.icon} side-menu__icon`"></i>
                  <span class="side-menu__label">
                    {{ mainmenuItem.title }}
                    <span
                        v-if="mainmenuItem.badge"
                        :class="`badge ${mainmenuItem.badgeColor} ms-1`"
                    >{{ mainmenuItem.badge }}</span
                    >
                  </span>
                </NuxtLink>
              </template>

              <!-- Élément neutre -->
              <template v-else-if="mainmenuItem?.type === 'empty'">
                <NuxtLink href="javascript:;" class="side-menu__item">
                  <i :class="`${mainmenuItem.icon} side-menu__icon`"></i>
                  <span class="side-menu__label">
                    {{ mainmenuItem.title }}
                    <span
                        v-if="mainmenuItem.badge"
                        :class="`badge ${mainmenuItem.badgeColor} ms-1`"
                    >{{ mainmenuItem.badge }}</span
                    >
                  </span>
                </NuxtLink>
              </template>

              <!-- Sous-menu -->
              <template v-else-if="mainmenuItem?.type === 'sub'">
                <RecursiveMenu
                    :menuData="mainmenuItem"
                    :toggleSubmenu="toggleSubmenu"
                    :HoverToggleInnerMenuFn="HoverToggleInnerMenuFn"
                    :setSubmenu="setSubmenu"
                    :level="level + 1"
                />
              </template>
            </li>
          </ul>

          <div class="slide-right" @click="rightArrowFn" id="slide-right">
            <svg
                xmlns="http://www.w3.org/2000/svg"
                fill="#7b8191"
                width="24"
                height="24"
                viewBox="0 0 24 24"
            >
              <path
                  d="M10.707 17.707 16.414 12l-5.707-5.707-1.414 1.414L13.586 12l-4.293 4.293z"
              />
            </svg>
          </div>
        </nav>
      </div>
    </PerfectScrollbar>
  </aside>

  <div ref="preventpagejump" class="prevent-page-jump"></div>
</template>

<script setup>
import { computed, watchEffect, ref, onMounted, onBeforeUnmount, watch, nextTick } from "vue";
import RecursiveMenu from "../UI/recursiveMenu.vue";
import { useMenu } from "@/composables/useMenu";
import { useRouter } from "vue-router";
import { useProfileStore } from "@/stores/profile";
import { useNotificationsStore } from "@/stores/notifications";
import { usePermissions } from "@/composables/usePermissions";

// Store
const profileStore = useProfileStore();
const notificationsStore = useNotificationsStore();
const studentSlug = computed(() => profileStore.profile?.etudiant?.slug || null);

// Router
const router = useRouter();

// Menu composable
const { allowedMenu, saveMenuState } = useMenu();

// Template refs
const preventpagejump = ref(null);
const _layoutObserver = ref(null);

// Reactive data
const setMenu = ref(false);
const menuOverflowed = ref(false);
const previousUrl = ref("/");
const menuReady = ref(false); // Pour éviter le flash lors du chargement
// menuDataRef supprimé - on utilise directement allowedMenu.value
const WindowPreSize = ref([window.innerWidth]);
const oldLi = ref(undefined);
const level = ref(0);
const Nested = ref([]);
const PreviousNumber = ref(null);
const hasParent = ref(false);
const hasParentLevel = ref(0);

// Computed - Utilise le nouveau système de menu
const visibleMenu = computed(() => {
  // Le nouveau système useMenu gère déjà le filtrage basé sur les permissions et groupes
  return allowedMenu.value || [];
});

const isMobile = computed(() => (WindowPreSize.value.at(-1) || window.innerWidth) <= 576);

// Assurer le refresh du badge mobile même si la cloche est masquée
watch(
  [studentSlug, () => notificationsStore.studentPushNotificationsEnabled],
  ([slug]) => {
    if (!notificationsStore.studentPushNotificationsEnabled) {
      notificationsStore.stopPolling()
      return
    }
    if (slug && notificationsStore.isConfigured) {
      // Sur mobile, on veut juste garder le badge à jour, sans toast répétitif.
      notificationsStore.startPolling(slug, { pollMs: 25000, showToast: true });
    }
  },
  { immediate: true }
);

// Methods
function findByKey(nodes, key) {
  for (const it of nodes || []) {
    if (it?.key === key) return it;
    const found = findByKey(it.children, key);
    if (found) return found;
  }
  return null;
}

function findParent(tree, targetKey) {
  for (const it of tree || []) {
    if (it.children && it.children.some((ch) => ch?.key === targetKey))
      return it;
    const p = findParent(it.children, targetKey);
    if (p) return p;
  }
  return null;
}

function toggleSubmenu(event, targetClone) {

  const html = document.documentElement;
  if (
      html.getAttribute("data-nav-style") === "icon-hover" ||
      html.getAttribute("data-nav-style") === "menu-hover"
  )
    return;
  const node = findByKey(allowedMenu.value, targetClone.key);
  if (!node) {
    return;
  }
  if (
      html.getAttribute("data-vertical-style") == "doublemenu" &&
      node.active
  )
    return;
  node.active = !node.active;
  if (node.active) {
    // Ne fermer que les menus de même niveau, pas les parents
    closeOtherMenusAtSameLevel(allowedMenu.value, node.key);
  } else if (html.getAttribute("data-vertical-style") == "doublemenu") {
    html.setAttribute("data-toggled", "double-menu-close");
  }
  setAncestorsActive(allowedMenu.value, node.key);
  if (node?.children && node.active) {
    if (
        html.getAttribute("data-vertical-style") == "doublemenu" &&
        html.getAttribute("data-toggled") != "double-menu-open"
    ) {
      html.setAttribute("data-toggled", "double-menu-open");
    }
  }

  // Sauvegarder l'état du menu après modification
  saveMenuState();
}

function handleAttributeChange(mutationsList) {
  for (const mutation of mutationsList) {
    if (
        mutation.type === "attributes" &&
        mutation.attributeName === "data-nav-layout"
    ) {
      const newValue = mutation.target.getAttribute("data-nav-layout");
      if (newValue === "vertical") {
        let currentPath = router.currentRoute.value.path.endsWith("/")
            ? router.currentRoute.value.path.slice(0, -1)
            : router.currentRoute.value.path;
        currentPath = !currentPath ? "/dashboard/ecommerce" : currentPath;
        setMenuUsingUrl(currentPath);
      } else {
        closeMenuFn();
      }
    }
  }
}

function setAncestorsActive(tree, targetKey) {
  const html = document.documentElement;
  const parent = findParent(tree, targetKey);
  if (parent) {
    parent.active = true;
    if (parent.active)
      html.setAttribute("data-toggled", "double-menu-open");
    setAncestorsActive(tree, parent.key);
  } else if (html.getAttribute("data-vertical-style") == "doublemenu") {
    html.setAttribute("data-toggled", "double-menu-close");
  }
}

function closeOtherMenus(tree, keepKey) {
  for (const it of tree || []) {
    if (it?.key !== keepKey) it.active = false;
    if (it.children?.length) closeOtherMenus(it.children, keepKey);
  }
}

function closeOtherMenusAtSameLevel(tree, keepKey) {
  // Trouver le parent du nœud à garder
  const parent = findParent(tree, keepKey);

  if (parent && parent.children) {
    // Fermer tous les enfants du parent sauf celui à garder
    parent.children.forEach(child => {
      if (child.key !== keepKey) {
        child.active = false;
      }
    });
  } else {
    // Si pas de parent, fermer tous les menus de niveau racine sauf celui à garder
    tree.forEach(item => {
      if (item.key !== keepKey) {
        item.active = false;
      }
    });
  }
}

function setSubmenu(event, targetClone) {

  const html = document.documentElement;
  if (
      html.getAttribute("data-nav-style") === "icon-hover" ||
      html.getAttribute("data-nav-style") === "menu-hover"
  )
    return;
  if (event?.ctrlKey) return;
  const targetKey = targetClone?.key;
  if (!targetKey) return;

  // reset
  const reset = (nodes) => {
    nodes?.forEach((n) => {
      n.active = false;
      n.selected = false;
      reset(n.children);
    });
  };
  reset(allowedMenu.value);

  // marque selected
  const node = findByKey(allowedMenu.value, targetKey);
  if (!node) {
    return;
  }
  node.selected = true;

  // remonte et ouvre parents
  let p = findParent(allowedMenu.value, node.key);
  while (p) {
    p.active = true;
    p.selected = true;
    p = findParent(allowedMenu.value, p.key);
  }

  // IMPORTANT: Toujours garder le nœud actuel actif s'il a des enfants
  // ou s'il est un parent d'un élément sélectionné
  if (node.children && node.children.length > 0) {
    node.active = true;
  }

  // Si le nœud a un parent, s'assurer que le parent reste ouvert
  const parent = findParent(allowedMenu.value, node.key);
  if (parent) {
    parent.active = true;
  }
}

function removeActiveOtherMenus(item) {
  if (!item) return;
  if (Array.isArray(item))
    item.forEach((val) => {
      val.active = false;
      val.selected = false;
    });
  item.active = false;
  item.selected = false;
  if (item.children?.length) removeActiveOtherMenus(item.children);
}

function closeMenuFn() {
  const closeRec = (items) => {
    items?.forEach((it) => {
      it.active = false;
      closeRec(it.children);
    });
  };
  closeRec(allowedMenu.value);
}

function HoverToggleInnerMenuFn(event, targetClone) {
  const html = document.documentElement;
  // Activer uniquement en mode icon-hover ou menu-hover
  if (
    html.getAttribute("data-nav-style") !== "icon-hover" &&
    html.getAttribute("data-nav-style") !== "menu-hover"
  ) {
    return;
  }

  const node = findByKey(allowedMenu.value, targetClone.key);
  if (!node) return;

  // Toggle le menu au survol
  if (!node.active) {
    // Fermer les autres menus du même niveau
    closeOtherMenusAtSameLevel(allowedMenu.value, node.key);
    node.active = true;
  }
}

function menuResizeFn() {
  WindowPreSize.value.push(window.innerWidth);
  if (WindowPreSize.value.length > 2) WindowPreSize.value.shift();
  if (WindowPreSize.value.length > 1) {
    if (WindowPreSize.value.at(-1) < 992 && WindowPreSize.value.at(-2) >= 992)
      document.documentElement.setAttribute("data-toggled", "close");
    if (WindowPreSize.value.at(-1) >= 992 && WindowPreSize.value.at(-2) < 992)
      document.documentElement.removeAttribute("data-toggled");
  }
}

function mainContentFn() {
  const html = document.documentElement;
  if (window.innerWidth < 992) {
    html.setAttribute("data-toggled", "close");
  } else if (html.getAttribute("data-nav-layout") === "horizontal") {
    closeMenuFn();
  }
  // en layout vertical, on ne ferme pas
}

function leftArrowFn() {
  let slideLeft = document.querySelector(".slide-left");
  let slideRight = document.querySelector(".slide-right");
  let menuNav = document.querySelector(".main-menu");
  let mainContainer1 = document.querySelector(".main-sidebar");
  let marginRightValue = Math.ceil(
      Number(
          window.getComputedStyle(menuNav).marginInlineStart.split("px")[0]
      )
  );
  let mainContainer1Width = mainContainer1.offsetWidth;
  if (menuNav.scrollWidth > mainContainer1.offsetWidth) {
    if (
        marginRightValue < 0 &&
        !(Math.abs(marginRightValue) < mainContainer1Width)
    ) {
      menuNav.style.marginInlineStart =
          Number(menuNav.style.marginInlineStart.split("px")[0]) +
          Math.abs(mainContainer1Width) +
          "px";
      slideRight.classList.remove("d-none");
    } else if (marginRightValue >= 0) {
      menuNav.style.marginInlineStart = "0px";
      slideLeft.classList.add("d-none");
      slideRight.classList.remove("d-none");
    } else {
      menuNav.style.marginInlineStart = "0px";
      slideLeft.classList.add("d-none");
      slideRight.classList.remove("d-none");
    }
  } else {
    menuNav.style.marginInlineStart = "0px";
    slideLeft.classList.add("d-none");
  }
  let element = document.querySelector(".main-menu > .slide.open");
  let element1 = document.querySelector(".main-menu > .slide.open >ul");
  if (element) element.classList.remove("open");
  if (element1) element1.style.display = "none";
}

function rightArrowFn() {
  let slideLeft = document.querySelector(".slide-left");
  let slideRight = document.querySelector(".slide-right");
  let menuNav = document.querySelector(".main-menu");
  let mainContainer1 = document.querySelector(".main-sidebar");
  let marginRightValue = Math.ceil(
      Number(
          window.getComputedStyle(menuNav).marginInlineStart.split("px")[0]
      )
  );
  let check = menuNav.scrollWidth - mainContainer1.offsetWidth;
  let mainContainer1Width = mainContainer1.offsetWidth;
  if (menuNav.scrollWidth > mainContainer1.offsetWidth) {
    if (Math.abs(check) > Math.abs(marginRightValue)) {
      if (
          !(
              Math.abs(check) >
              Math.abs(marginRightValue) + mainContainer1Width
          )
      ) {
        mainContainer1Width = Math.abs(check) - Math.abs(marginRightValue);
        slideRight.classList.add("d-none");
      }
      menuNav.style.marginInlineStart =
          Number(menuNav.style.marginInlineStart.split("px")[0]) -
          Math.abs(mainContainer1Width) +
          "px";
      slideLeft.classList.remove("d-none");
    }
  }
  let element = document.querySelector(".main-menu > .slide.open");
  let element1 = document.querySelector(".main-menu > .slide.open >ul");
  if (element) element.classList.remove("open");
  if (element1) element1.style.display = "none";
}

function setMenuUsingUrl(currentPath) {
  // Attendre que le menu soit prêt
  if (!allowedMenu.value || allowedMenu.value.length === 0) {
    return;
  }


  // reset
  const reset = (nodes) => {
    nodes?.forEach((n) => {
      n.selected = false;
      reset(n.children);
    });
  };
  reset(allowedMenu.value);

  // Cas spécial : Si un étudiant est sur sa propre fiche, mapper vers "Ma fiche"
  let pathToUse = currentPath;
  const { isStudent } = usePermissions();
  if (isStudent.value && profileStore.profile) {
    // Vérifier si le chemin est une fiche étudiant : /admin/academy/students/{slug}
    const studentDetailPattern = /^\/admin\/academy\/students\/[^/]+$/;
    if (studentDetailPattern.test(currentPath)) {
      // Extraire le slug de l'URL
      const pathParts = currentPath.split('/').filter(Boolean);
      const slugFromUrl = pathParts[pathParts.length - 1];
      
      // Vérifier si c'est le slug de l'étudiant connecté
      const studentSlug = profileStore.profile?.etudiant?.slug;
      if (slugFromUrl === studentSlug) {
        // Mapper vers "Ma fiche" au lieu du chemin de détail
        pathToUse = '/admin/academy/students/me';
      }
    }
  }

  // trouve node - amélioration pour gérer les chemins qui commencent par le path du menu
  const findByPath = (nodes, path) => {
    let bestMatch = null;
    let bestMatchLength = 0;

    // D'abord, collecter tous les matches possibles (y compris dans les enfants)
    const collectMatches = (nodes, path, matches = []) => {
      for (const n of nodes || []) {
        if (n.path) {
          // Correspondance exacte - priorité maximale
          if (n.path === path) {
            matches.push({ node: n, length: n.path.length, exact: true });
            continue;
          }
          // Correspondance par préfixe (pour gérer les sous-chemins)
          // Vérifier que le chemin commence par n.path suivi de / ou est exactement n.path
          if (path === n.path || path.startsWith(n.path + '/')) {
            matches.push({ node: n, length: n.path.length, exact: false });
          }
        }
        if (n.children) {
          collectMatches(n.children, path, matches);
        }
      }
      return matches;
    };

    const allMatches = collectMatches(nodes, path);
    
    // Trouver le match exact d'abord
    const exactMatch = allMatches.find(m => m.exact);
    if (exactMatch) {
      return exactMatch.node;
    }

    // Sinon, trouver le match le plus long (le plus spécifique)
    if (allMatches.length > 0) {
      allMatches.sort((a, b) => b.length - a.length);
      return allMatches[0].node;
    }

    return null;
  };

  const node = findByPath(allowedMenu.value, pathToUse);
  if (!node) {
    return;
  }

  node.selected = true;

  // remonte & ouvre
  let p = findParent(allowedMenu.value, node.key);
  while (p) {
    p.active = true;
    p.selected = true;
    p = findParent(allowedMenu.value, p.key);
  }

  // S'assurer que le menu parent reste ouvert même si on navigue vers un enfant
  if (node.path && node.path !== currentPath) {
    // Si le nœud trouvé n'est pas exactement le chemin actuel,
    // c'est probablement un parent, on le garde ouvert
    node.active = true;
  }

  // S'assurer que tous les parents restent ouverts
  let currentParent = findParent(allowedMenu.value, node.key);
  while (currentParent) {
    currentParent.active = true;
    currentParent = findParent(allowedMenu.value, currentParent.key);
  }

  // Sauvegarder l'état du menu après modification
  saveMenuState();
}

function menuscrollFn() {
  let html = document.documentElement;
  let navLayout = html.getAttribute("data-nav-layout") == "horizontal";
  let menuPosition =
      html.getAttribute("data-menu-position") == "scrollable";
  let header = document.querySelector(".app-header")?.clientHeight || 0;
  window.onscroll = () => {
    if (
        !menuPosition &&
        preventpagejump.value &&
        navLayout &&
        window.innerWidth >= 992
    ) {
      if (window.scrollY > header) {
        preventpagejump.value.style.height = header + "px";
        menuOverflowed.value = true;
      } else {
        preventpagejump.value.style.height = "0px";
        menuOverflowed.value = false;
      }
    }
  };
}

// Watch pour réagir quand le menu devient disponible ou change
let menuLength = ref(0);
watch(() => allowedMenu.value, (newMenu, oldMenu) => {
  const newLength = newMenu?.length || 0;
  const oldLength = oldMenu?.length || 0;

  // Détecter si le menu a changé (nouveau menu ou taille différente)
  if (newLength > 0 && (newLength !== menuLength.value || oldLength === 0)) {

    menuLength.value = newLength;

    // Utiliser nextTick pour s'assurer que le DOM et les états sont à jour
    nextTick(() => {
      const currentUrl = router.currentRoute.value.path.endsWith("/")
          ? router.currentRoute.value.path.slice(0, -1)
          : router.currentRoute.value.path;
      setMenuUsingUrl(currentUrl);
    });
  }
}, { immediate: true, deep: false }); // deep: false pour ne pas réagir aux changements d'états active/selected

// Lifecycle hooks
onMounted(() => {
  // Essayer d'initialiser le menu avec l'URL actuelle
  const currenUrl = router.currentRoute.value.path.endsWith("/")
      ? router.currentRoute.value.path.slice(0, -1)
      : router.currentRoute.value.path;
  setMenuUsingUrl(currenUrl);

  // Surveiller les changements de route
  router.afterEach((to) => {
    let currentPath = to.path.endsWith("/")
        ? to.path.slice(0, -1)
        : to.path;
    currentPath = !currentPath ? "/dashboard/ecommerce" : currentPath;
    if (currentPath !== previousUrl.value) {
      setMenuUsingUrl(currentPath);
      previousUrl.value = currentPath;
    }
  });

  const html = document.documentElement;
  if (
      html.getAttribute("data-nav-layout") == "horizontal" ||
      html.getAttribute("data-nav-style") == "menu-hover" ||
      html.getAttribute("data-nav-style") == "icon-hover"
  ) {
    closeMenuFn();
  }

  window.addEventListener("resize", menuResizeFn);
  window.addEventListener("scroll", menuscrollFn);
  document
      .querySelector(".main-content")
      ?.addEventListener("click", mainContentFn);

  if (window.innerWidth < 992)
    document.documentElement.setAttribute("data-toggled", "close");
  else if (
      document.documentElement.getAttribute("data-nav-layout") == "horizontal"
  )
    closeMenuFn();

  // 🔒 MutationObserver: protège si la méthode n'est pas dispo
  if (typeof MutationObserver !== "undefined") {
    const cb =
        typeof handleAttributeChange === "function"
            ? (mutations) => handleAttributeChange(mutations)
            : () => {};
    _layoutObserver.value = new MutationObserver(cb);
    _layoutObserver.value.observe(document.documentElement, {
      attributes: true,
      attributeFilter: ["data-nav-layout"],
    });
  }
});

onBeforeUnmount(() => {
  window.removeEventListener("resize", menuResizeFn);
  if (_layoutObserver.value) _layoutObserver.value.disconnect();
});
</script>

<style>
.slide-menu {
  display: block;
}
</style>
